import type { QueryClient } from '@tanstack/react-query';
import type { TicketDetails, TicketStatus } from './types';

/**
 * O que o evento `message.received` carrega e o header consome. Campos
 * opcionais porque emissores diferentes (api, workers) variam o payload.
 */
export interface EventoComHeader {
  ticket_id: number;
  ticket_status?: TicketStatus | undefined;
  /** Timestamp da mensagem — inbound é o novo `last_inbound_at` do canal. */
  received_at?: string | undefined;
  created_at?: string | undefined;
  direction?: 'inbound' | 'outbound' | undefined;
}

/**
 * Escreve no header em cache (`['ticket', id]`) o que o evento de INBOUND já
 * traz: `status` e `contact.last_inbound_at`. É o que evita o flash do banner
 * "janela 24h expirou" ao abrir a conversa — sem isto, o TanStack pinta o
 * cache velho (bloqueado) e só ~30ms depois o refetch do invalidate libera.
 *
 * NÃO substitui o invalidate: quem chama continua marcando o header stale, e
 * o refetch confirma a verdade do servidor em background (sem efeito visual,
 * porque os valores já batem). Mesmo padrão do motor (`onTicketChanged`
 * escreve o cartão; aqui escrevemos o que o evento de mensagem sabe).
 *
 * Outbound não mexe em janela/status por esta via → no-op. Header fora do
 * cache (conversa nunca aberta) → no-op, o refetch pós-invalidate cobre.
 */
export function escreveHeaderDoInbound(qc: QueryClient, evt: EventoComHeader): void {
  if (evt.direction === 'outbound') return;
  const novoInbound = evt.received_at ?? evt.created_at ?? null;
  qc.setQueryData<TicketDetails>(['ticket', evt.ticket_id], (antigo) =>
    antigo
      ? {
          ...antigo,
          ...(evt.ticket_status && { status: evt.ticket_status }),
          ...(novoInbound && {
            contact: { ...antigo.contact, last_inbound_at: novoInbound },
          }),
        }
      : antigo,
  );
}
